/** A separate local database per account; the guest library is never uploaded implicitly. */
import {
  createDatabase,
  createIndexedDbJournalStorage,
  createLocalAttachmentStore,
  createLocalMapCatalog,
  isIndexedDbAvailable,
  type IndexedDbJournalStorage,
  type LocalAttachmentStore,
  type LocalMapCatalog,
  type OrbitMindDatabase,
} from '@orbit-mind/offline-engine/indexeddb';
import { legacyOwner, storageScope } from '@orbit-mind/shared-types';

export interface LocalStorageLayer {
  readonly db: OrbitMindDatabase;
  readonly journal: IndexedDbJournalStorage;
  readonly catalog: LocalMapCatalog;
  readonly attachments: LocalAttachmentStore;
}

let accountId: string | null = null;
const layers = new Map<string, LocalStorageLayer>();
let activation = 0;
export function storageAccount(): string | null {
  return accountId;
}

export function localStorageLayer(): LocalStorageLayer {
  return layerFor(accountId);
}

function layerFor(userId: string | null): LocalStorageLayer {
  const scope = storageScope(userId);
  const existing = layers.get(scope);
  if (existing !== undefined) return existing;

  const db = createDatabase(`orbit-mind-${scope}`);
  const layer = {
    db,
    journal: createIndexedDbJournalStorage(db),
    catalog: createLocalMapCatalog(db),
    attachments: createLocalAttachmentStore(db),
  };
  layers.set(scope, layer);
  return layer;
}

/** Copy once without touching the legacy database, including unsent commands and file bytes. */
export async function activateStorageAccount(userId: string | null): Promise<void> {
  const attempt = ++activation;
  const target = layerFor(userId);
  if ((await target.db.settings.get('legacy-imported')) === undefined) {
    const source = createDatabase();
    try {
      const maps = (await source.maps.toArray()).filter((row) => legacyOwner(row.map) === userId);
      const ids = new Set(maps.map((row) => row.mapId));
      const [journal, checkpoints, attachments, assets, lifecycle] = await Promise.all([
        source.journal
          .where('mapId')
          .anyOf([...ids])
          .toArray(),
        source.checkpoints
          .where('mapId')
          .anyOf([...ids])
          .toArray(),
        source.attachments
          .where('mapId')
          .anyOf([...ids])
          .toArray(),
        source.assets
          .where('mapId')
          .anyOf([...ids])
          .toArray(),
        source.lifecycle
          .where('mapId')
          .anyOf([...ids])
          .toArray(),
      ]);
      await target.db.transaction('rw', target.db.tables, async () => {
        for (const row of maps) {
          if ((await target.db.maps.get(row.mapId)) !== undefined) ids.delete(row.mapId);
          else await target.db.maps.put(row);
        }
        await target.db.journal.bulkPut(journal.filter((row) => ids.has(row.mapId)));
        await target.db.checkpoints.bulkPut(checkpoints.filter((row) => ids.has(row.mapId)));
        await target.db.attachments.bulkPut(attachments.filter((row) => ids.has(row.mapId)));
        await target.db.assets.bulkPut(assets.filter((row) => ids.has(row.mapId)));
        await target.db.lifecycle.bulkPut(lifecycle.filter((row) => ids.has(row.mapId)));
        await target.db.settings.put({ key: 'legacy-imported', value: '1' });
      });
    } finally {
      source.close();
    }
  }
  if (attempt === activation) accountId = userId;
}

/** Test cleanup across all opened namespaces; never called by product code. */
export async function clearTestStorage(): Promise<void> {
  await whenStorageIsIdle();
  for (const layer of layers.values()) for (const table of layer.db.tables) await table.clear();
  accountId = null;
}

/**
 * Work that outlives the component that started it.
 *
 * Closing a map flushes and checkpoints, and that happens in an unmount cleanup — after which
 * React has already moved on to the next screen. If that screen is the library, it reads the
 * catalogue while the map it is about to list is still writing its row, and shows numbers that
 * were true a minute ago.
 *
 * So background writes are chained here, and a screen that reads what they write waits for them
 * first. One chain rather than a set, because these are writes to one database and running them
 * in order is what the database is going to do anyway.
 */
let inFlight: Promise<unknown> = Promise.resolve();

export function trackBackgroundWrite(work: Promise<unknown>): void {
  inFlight = inFlight.then(
    () => work,
    () => work,
  );
}

/** Resolves once every tracked write has landed — or failed, which is also finished. */
export function whenStorageIsIdle(): Promise<void> {
  return inFlight.then(
    () => undefined,
    () => undefined,
  );
}

/**
 * Can this browser store anything at all?
 *
 * Private windows in some browsers, and some embedded webviews, answer no. The editor must say
 * so before the user types a word into something that cannot keep it, rather than after.
 */
export function storageIsAvailable(): boolean {
  return isIndexedDbAvailable();
}
